import firebase from "firebase/app";
import "firebase/auth";
import "firebase/firestore";
import "firebase/storage";
import { nanoid } from "nanoid";
import { isDefined, toSafeFilename } from "../components/helpers";

import {
  FIREBASE_CONFIG,
  USHER,
  SNIPPET,
  ERRORS,
  JobStatus,
  ContentType,
  PER_PAGE,
  CLOUD_BUILD_OPERATIONS,
} from "../constants";
import { toThumbnailFileName } from "../utils";
import { replaceQueryParam } from "./utils";

firebase.initializeApp(FIREBASE_CONFIG);

export const db = firebase.firestore();
export const auth = firebase.auth();
export const storage = firebase.storage();

export const DEFAULT_LIMIT = PER_PAGE;
export const firestoreCollectionLimitReached = {};

export async function getItems(collection: string, orderBy: string, page = 1) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  console.log(collection, auth.currentUser.uid);
  const data = await db
    .collection(collection)
    .where("uid", "==", auth.currentUser.uid)
    .orderBy(orderBy, "desc")
    .limit(page * PER_PAGE)
    .get();
  return data.docs;
}

export function getItemAndListen(
  collection: string,
  itemId: string,
  callback: Function
) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const unsubscribe = db
    .collection(collection)
    .doc(itemId)
    .onSnapshot((snap) => {
      callback(
        {
          id: itemId,
          ...snap.data(),
        },
        unsubscribe
      );
    });
}

/* ********************************** posts ************************************ */

export async function getPosts() {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const data = await db
    .collection("posts")
    .where("uid", "==", auth.currentUser.uid)
    .get();
  return data.docs;
}
export async function storeNewCampaign(title: string) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const docRef = await db.collection("posts").add({
    uid: auth.currentUser.uid,
    title,
    createdAt: new Date(),
  });
  const docSnapshot = await docRef.get();
  return {
    id: docSnapshot.id,
    ...docSnapshot.data(),
  };
}
export async function editCampaign(id: string, updates: Object) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  return db
    .collection("posts")
    .doc(id)
    .set(
      {
        ...updates,
        modifiedAt: new Date(),
      },
      { merge: true }
    );
}
export async function saveContent(postId: string, content: Content) {
  await db.runTransaction(async (transaction) => {
    const ref = db.doc(`posts/${postId}`);
    const doc = await transaction.get(ref);
    const contentData = (await doc.get("content")) || [];
    if (!content.id) {
      // new one
      content.id = nanoid(10);
      content.date = new Date();
      contentData.push(content);
    } else {
      // editing
      const idx = contentData.findIndex((c) => c.id === content.id);
      if (idx >= 0) {
        contentData[idx] = { ...contentData[idx], ...content };
      } else {
        contentData.push(content);
      }
    }
    transaction.update(ref, { content: contentData });
  });
  return content;
}
export async function deleteContent(postId: string, content: Content) {
  await db.runTransaction(async (transaction) => {
    const ref = db.doc(`posts/${postId}`);
    const doc = await transaction.get(ref);
    let contentData = (await doc.get("content")) || [];
    if (content.id) {
      contentData = contentData.filter((c) => c.id !== content.id);
    }
    transaction.update(ref, { content: contentData });
  });
  try {
    if (content.type === ContentType.FILE) {
      await deleteFile(content.value);
    }
    if (content.thumbnail) {
      await deleteFile(toThumbnailFileName(content.value));
    }
  } catch (err) {
    console.error(err);
  }
}
export async function moveContent(
  postId: string,
  contentId: string,
  direction: "up" | "down"
) {
  await db.runTransaction(async (transaction) => {
    const ref = db.doc(`posts/${postId}`);
    const doc = await transaction.get(ref);
    let contentData = (await doc.get("content")) || [];
    for (let i = 0; i < contentData.length; i++) {
      if (contentData[i].id === contentId && direction === "down") {
        const tmp = contentData[i - 1];
        if (tmp) {
          contentData[i - 1] = contentData[i];
          contentData[i] = tmp;
          break;
        }
      }
      if (contentData[i].id === contentId && direction === "up") {
        const tmp = contentData[i + 1];
        if (tmp) {
          contentData[i + 1] = contentData[i];
          contentData[i] = tmp;
          break;
        }
      }
    }
    transaction.update(ref, { content: [...contentData] });
  });
}
export async function deleteCampaign(post: Post) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  // cleaning post's jobs
  const jobs = await db
    .collection("jobs")
    .where("postId", "==", post.id)
    .where("uid", "==", auth.currentUser.uid)
    .get();
  jobs.docs.forEach((doc) => deleteJob(doc.id));
  // deleting posts files
  if (post.content && post.content.length > 0) {
    for (let i = 0; i < post.content.length; i++) {
      const c = post.content[i];
      if (c.type === ContentType.FILE) {
        try {
          await deleteFile(c.value);
          if (c.thumbnail) {
            await deleteFile(toThumbnailFileName(c.value));
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
  }
  await db.collection("posts").doc(post.id).delete();
}

/* ****************************** user ********************************** */

export function signIn(callback: Function) {
  auth.onAuthStateChanged(async function (user) {
    if (user) {
      await db.collection("users").doc(user.uid).set(
        {
          lastLogin: new Date(),
          email: user.email,
        },
        { merge: true }
      );
      const userData = (
        await db.collection("users").doc(user.uid).get()
      ).data() as User;
      userData.uid = user.uid;
      callback(userData);
    } else {
      callback(null);
    }
  });
}
export function signOut() {
  return auth.signOut();
}
export async function register(data: RegisterData) {
  return auth.createUserWithEmailAndPassword(data.username, data.password);
}
export async function login(data: LoginData) {
  return auth.signInWithEmailAndPassword(data.username, data.password);
}

/* ****************************** destinations ********************************** */

export async function addDestination(
  user: User,
  type: DESTINATION_TYPE,
  data: Object,
  meta: Object
): Promise<Destination> {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  // adding to the destinations collection
  const newDoc = await db
    .collection("destinations")
    .add({ uid: user.uid, type, ...data });
  // adding to the users collection
  const newDestination = { id: newDoc.id, type, meta } as Destination;
  await db.runTransaction(async (transaction) => {
    const ref = db.doc(`users/${user.uid}`);
    const doc = await transaction.get(ref);
    const destinationsData = (await doc.get("destinations")) || [];
    destinationsData.push(newDestination);
    transaction.update(ref, { destinations: destinationsData });
  });
  return newDestination;
}
export async function deleteDestination(id: string, user: User) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const newDestinations = user.destinations.filter((d) => d.id !== id);
  await db.collection("users").doc(user.uid).set(
    {
      destinations: newDestinations,
    },
    { merge: true }
  );
  await db.collection("destinations").doc(id).delete();
  // cleaning post's jobs
  const jobs = await db
    .collection("jobs")
    .where("destinationId", "==", id)
    .where("uid", "==", auth.currentUser.uid)
    .get();
  // jobs.docs.forEach((doc) => deleteJob(doc.id));
}

/* ****************************** jobs ********************************** */

export function getJobs(postId: string, callback: Function) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  db.collection("jobs")
    .where("postId", "==", postId)
    .where("uid", "==", auth.currentUser.uid)
    .onSnapshot((querySnapshot) => {
      callback(querySnapshot.docs);
    });
}
export async function newJob(
  postId: string,
  content: string[],
  destinationId: string,
  destinationMeta: Object,
  date: Date
): Promise<string> {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const docRef = await db.collection("jobs").add(
    Object.assign(
      {
        uid: auth.currentUser.uid,
        postId,
        destinationId,
        content,
        date,
        status: JobStatus.PENDING,
      },
      destinationMeta && { destinationMeta }
    )
  );
  const docSnapshot = await docRef.get();
  createJobTask(docSnapshot.id, date);
  return docSnapshot.id;
}
export async function deleteJob(id: string) {
  await db.collection("jobs").doc(id).delete();
}
export async function createJobTask(jobId: string, date) {
  const payload: CloudTaskData = {
    queue: "send",
    payload: {
      id: jobId,
      date,
    },
  };
  const res = await fetch(USHER, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify(payload),
    headers: {
      "Content-Type": "application/json",
    },
  });
  await res.json();
}
export async function createSnippetRequest(
  code: string,
  post: string,
  content: string,
  options: CodeSnippetOptions
) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const res = await fetch(SNIPPET, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify({
      uid: auth.currentUser.uid,
      code,
      post,
      content,
      theme: options.theme || "Cobalt2",
      language: options.language || "javascript",
      frame: isDefined(options.frame) ? options.frame : true,
      lineNumbers: isDefined(options.lineNumbers) ? options.lineNumbers : true,
      filename: isDefined(options.filename)
        ? options.filename
        : toSafeFilename(nanoid()),
      backgroundColor: options.backgroundColor || "#999999",
    }),
    headers: {
      "Content-Type": "application/json",
    },
  });
  await res.json();
}

/* ****************************** files ********************************** */

export function uploadFile(
  file: File,
  post,
  onSuccess: Function,
  onProgress: Function,
  onError: Function
) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const originalFileName = file.name;
  const ext = "" + originalFileName.split(".").pop();
  const filename = `${nanoid()}.${ext.toLowerCase()}`;
  const path = `${auth.currentUser.uid}/${post.id}/${filename}`;
  const ref = firebase.storage().ref(path);
  const task = ref.put(file);

  task.then(async (snapshot) => {
    let url = await getDownloadURL(path);
    onSuccess(path, {
      name: originalFileName,
      contentType: file.type,
      size: file.size,
      url,
    });
  });
  task.on("state_changed", (snapshot) => {
    let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
    onProgress(progress);
  });
  task.catch((err) => {
    onError(err);
  });
  return task;
}
export function updateImageFile(
  blob,
  content: Content,
  post: Post,
  onSuccess: Function,
  onProgress: Function,
  onError: Function
) {
  if (!auth.currentUser) {
    throw new Error(ERRORS.NO_AUTHENTICATION);
  }
  const ref = firebase.storage().ref(content.value);
  const task = ref.put(blob);
  task.then(async (snapshot) => {
    snapshot.ref.getDownloadURL().then(async (url) => {
      const newToken =
        (url.split("?")[1] || "")
          .split("&")
          .find((qStr) => qStr.match(/^token/))
          .split("=")[1] || "";
      if (newToken) {
        await saveContent(post.id, {
          id: content.id,
          actions: ["CREATING_THUMBNAIL"],
          meta: {
            ...(content.meta || {}),
            url: replaceQueryParam(content.meta?.url || "", "token", newToken),
          },
        });
        createCloudBuildOp({
          operation: CLOUD_BUILD_OPERATIONS.create_thumbnail,
          payload: {
            postId: post.id,
            contentId: content.id as string,
          },
        });
        onSuccess();
      } else {
        console.error("There is no token in the new URL.");
        onError();
      }
    });
  });
  task.on("state_changed", (snapshot) => {
    let progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
    onProgress(progress);
  });
  task.catch((err) => {
    onError(err);
  });
  return task;
}
export async function getDownloadURL(path: string) {
  // This works only because the bucket in Google Cloud Storage is made public.
  // Check out this https://stackoverflow.com/a/61129057/642670
  return `https://storage.googleapis.com/${FIREBASE_CONFIG.storageBucket}/${path}`;
}
export async function createCloudBuildOp(config: CloudBuildData) {
  const res = await fetch(USHER, {
    method: "POST",
    mode: "cors",
    body: JSON.stringify(config),
    headers: {
      "Content-Type": "application/json",
    },
  });
  await res.json();
}
export async function deleteFile(path: string) {
  return firebase.storage().ref(path).delete();
}

/* ****************************** utils ********************************** */
export function firebaseDateObjToJSDate(obj) {
  return new firebase.firestore.Timestamp(
    obj._seconds,
    obj._nanoseconds
  ).toDate();
}
